import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Trash2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useLocation } from "wouter"
import { api, ApiRequestError } from "@/lib/api"
import { cn } from "@/lib/utils"
import type { ArealResponse } from "@fohet/caravan-booker-shared/types"
import { inputClass, labelClass, sectionClass } from "@/lib/styleClasses"
import { useAdminArealStore } from "@/store/adminArealStore"

export const DangerZoneSection = ({ areal }: { areal: ArealResponse }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [, navigate] = useLocation()
    const clearSelectedArealId = useAdminArealStore((s) => s.clearSelectedArealId)
    const [confirmName, setConfirmName] = useState("")

    const deleteMutation = useMutation({
        mutationFn: () => api.delete(`/areals/${areal.id}`),
        onSuccess: () => {
            toast.success(t("admin.settings.deleteArealToastDeleted"))
            clearSelectedArealId()
            queryClient.invalidateQueries({ queryKey: ["admin", "areals"] })
            navigate("/admin")
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.deleteArealToastFailed"))
        },
    })

    return (
        <div className={cn(sectionClass, "border-red-100 bg-red-50/30")}>
            <h2 className="text-lg font-black text-red-700">{t("admin.settings.sectionDangerZone")}</h2>
            <p className="text-sm font-bold text-slate-600">{t("admin.settings.deleteArealDescription")}</p>
            <div className="space-y-2">
                <label className={cn(labelClass, "text-red-600")}>{t("admin.settings.deleteArealConfirm")}</label>
                <input
                    type="text"
                    value={confirmName}
                    onChange={(e) => setConfirmName(e.target.value)}
                    placeholder={areal.name}
                    className={cn(inputClass, "border-red-200 focus:ring-red-400")}
                />
            </div>
            <div className="flex justify-end">
                <button
                    type="button"
                    disabled={confirmName !== areal.name || deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate()}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-red-500 text-white font-black text-sm hover:bg-red-600 transition-colors active:scale-95 disabled:opacity-40 shadow-md"
                >
                    <Trash2 className="size-3.5" />
                    {deleteMutation.isPending
                        ? t("admin.settings.deleteArealDeleting")
                        : t("admin.settings.deleteArealButton")}
                </button>
            </div>
        </div>
    )
}
